/**
 * Per-route wild encounter tables.
 *
 * Distribution mirrors Gen-3 FireRed's Route 1 grass entry: Pidgey/Rattata
 * 50/50 across levels 2-5 (canonical 12-slot table flattened to 2 weighted
 * entries). Towns (e.g. Pallet) have no table and `pickEncounter` returns
 * null for them.
 *
 * Encounter rate tuned to 0.10 for v0.1 (Phase 15b balance pass) — slightly
 * above the cartridge canonical ~0.08 (21/256) so progression doesn't feel
 * like wading, but well below the dev-friendly 0.2 we shipped through Phase
 * 12. Forest stays denser at 0.15 to read as a step-up zone.
 *
 * The WorldScene calls `pickEncounter(mapId, seedStore.rngFor('encounter'))`
 * so encounter rolls are deterministic from the master seed (Phase 11). The
 * `Math.random` default below is now only hit by tests that don't care about
 * determinism — production paths inject the seeded source.
 */
import { SPECIES_TO_DEX } from '../battle/teamFromParty';

export interface EncounterEntry {
  /** @pkmn id, lowercase. Must be a key in SPECIES_TO_DEX (sprite available). */
  species: string;
  /** Relative weight; the sum across entries doesn't need to be 100. */
  weight: number;
  levelMin: number;
  /** Inclusive upper bound; must be >= levelMin. */
  levelMax: number;
}

export interface EncounterTable {
  /** Override the global GRASS_ENCOUNTER_RATE; 0..1. Tuned per zone in
   *  Phase 15b balance pass (was 0.2 placeholder during dev). */
  rate: number;
  entries: EncounterEntry[];
}

/**
 * Region/map short name (e.g. "Route1") → table. Rekeyed from the long
 * cartridge `MAP_*` form to the short runtime-canonical name as part of
 * seamless-world Step 3 — every per-map state set (`routesDrafted`,
 * data tables) uses the short name post-PR. The `MAP_*` form is now only
 * a converter-internal artifact.
 *
 * Towns intentionally have no entry; encounters can't fire there because
 * grass tiles don't exist on town maps. `encounterRate` and `pickEncounter`
 * both fall back gracefully when a key is missing.
 */
export const ENCOUNTER_TABLES: Record<string, EncounterTable> = {
  // Routes retuned 0.10 → 0.08 in the same playtest pass.
  Route1: {
    rate: 0.08,
    entries: [
      { species: 'pidgey', weight: 50, levelMin: 2, levelMax: 5 },
      { species: 'rattata', weight: 50, levelMin: 2, levelMax: 4 },
    ],
  },
  // Cartridge Route 2 wilds: Pidgey + Rattata (no exclusive grass mons).
  // Same rate as Route 1 for consistency. Route 2 is the corridor between
  // Viridian and the Forest, so encountering familiar species here makes
  // sense — the Forest is where things change up.
  Route2: {
    rate: 0.08,
    entries: [
      { species: 'pidgey', weight: 50, levelMin: 3, levelMax: 5 },
      { species: 'rattata', weight: 50, levelMin: 3, levelMax: 5 },
    ],
  },
  // Cartridge Viridian Forest: Caterpie + Weedle dominate, Pikachu rare.
  // We surface Pikachu at 10% weight so a forest run has a chance to
  // grab one without a draft pick. Levels span the cartridge band 3-5.
  // Playtest round 2 (2026-08-02): 0.15 chained back-to-back encounters
  // ("every step, a couple in a row") — retuned to 0.09, just under the
  // routes, because forest crossings are LONG; density comes from the
  // trip length, not the per-step rate. Pairs with the 4-step post-
  // battle grace in WorldScene.checkEncounter.
  ViridianForest: {
    rate: 0.09,
    entries: [
      { species: 'caterpie', weight: 45, levelMin: 3, levelMax: 5 },
      { species: 'weedle', weight: 45, levelMin: 3, levelMax: 5 },
      { species: 'pikachu', weight: 10, levelMin: 3, levelMax: 5 },
    ],
  },
};

// Legacy `MAP_*` aliases for `encounter-determinism.test.ts`, which is the
// seamless-world PR's sacred CI gate (handoff: byte-identical, no edits).
// The test passes literal long-form ids ('MAP_ROUTE1'); these aliases let
// the rekeyed short-form tables continue serving those calls without
// changing the snapshot. Once the gate test is updated to pass short
// names, delete this block.
const LEGACY_MAP_ALIASES: Record<string, string> = {
  MAP_ROUTE1: 'Route1',
  MAP_ROUTE2: 'Route2',
  MAP_VIRIDIAN_FOREST: 'ViridianForest',
};
for (const [legacy, short] of Object.entries(LEGACY_MAP_ALIASES)) {
  const table = ENCOUNTER_TABLES[short];
  if (table) ENCOUNTER_TABLES[legacy] = table;
}

// Module-load assertion: every species in every table must have a sprite mapping.
// Catches typos / future additions before they crash a battle.
for (const [mapId, table] of Object.entries(ENCOUNTER_TABLES)) {
  for (const entry of table.entries) {
    if (!(entry.species in SPECIES_TO_DEX)) {
      throw new Error(
        `encounters.ts: ${mapId} references unknown species '${entry.species}' (not in SPECIES_TO_DEX)`,
      );
    }
    if (entry.levelMin > entry.levelMax) {
      throw new Error(
        `encounters.ts: ${mapId}/${entry.species} has levelMin (${entry.levelMin}) > levelMax (${entry.levelMax})`,
      );
    }
    if (entry.weight <= 0) {
      throw new Error(
        `encounters.ts: ${mapId}/${entry.species} has non-positive weight ${entry.weight}`,
      );
    }
  }
}

/**
 * Pick a random encounter from the table for the given map id. Returns null
 * if the map has no encounter table OR the table is empty. Pass an optional
 * rng (() => number returning [0, 1)). Production callers pass
 * `seedStore.rngFor('encounter')`; tests pass a fixed-roll lambda.
 */
export function pickEncounter(
  mapId: string,
  rng: () => number = Math.random,
): EncounterEntry | null {
  const table = ENCOUNTER_TABLES[mapId];
  if (!table || table.entries.length === 0) return null;

  const totalWeight = table.entries.reduce((sum, e) => sum + e.weight, 0);
  if (totalWeight <= 0) return null;

  // Weighted pick. Two rng draws: one for species, one for level — keeps the
  // level bound independent of species choice so adjusting one band doesn't
  // shift the species roll's bit pattern downstream.
  let roll = rng() * totalWeight;
  let chosen: EncounterEntry | undefined;
  for (const entry of table.entries) {
    roll -= entry.weight;
    if (roll < 0) {
      chosen = entry;
      break;
    }
  }
  // Floating-point edge: if all subtractions land us at exactly 0 the loop
  // exits without setting `chosen`. Fall back to the last entry.
  if (!chosen) chosen = table.entries[table.entries.length - 1]!;

  const span = chosen.levelMax - chosen.levelMin + 1;
  const level = chosen.levelMin + Math.floor(rng() * span);
  return {
    species: chosen.species,
    weight: chosen.weight,
    levelMin: level,
    levelMax: level,
  };
}

/**
 * Encounter rate (0..1) for a map's grass — falls back to `defaultRate` if
 * the map has no entry. Used by the WorldScene step-encounter check.
 */
export function encounterRate(mapId: string, defaultRate: number): number {
  return ENCOUNTER_TABLES[mapId]?.rate ?? defaultRate;
}
